import { CheckCircle2, XCircle, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { HalfCircle } from "@/components/ui/HalfCircle";
import { ReportCard } from "./ReportCard";
import { ReportBadge } from "./ReportBadge";
import { useState } from "react";
import { CitationBadges, CitationModal } from "@/components/citations";
import type { GroundingSource } from "@/services/api";

type Score = "yes" | "partially" | "no" | "insufficient";

interface CriterionCardProps {
  title: string;
  subtitle: string;
  coreQuestion?: string;
  score: Score;
  assessment: string;
  tradeoff: {
    gain?: string;
    giveUp?: string;
  };
  questions?: string[];
  icon: React.ReactNode;
  sources?: GroundingSource[];
  isEditing?: boolean;
  onUpdate?: (updates: { score?: Score; assessment?: string; tradeoff?: { gain?: string; giveUp?: string } }) => void;
}

export function CriterionCard({
  title,
  subtitle,
  coreQuestion,
  score,
  assessment,
  tradeoff,
  questions,
  icon,
  sources,
  isEditing,
  onUpdate,
}: CriterionCardProps) {
  const [expanded, setExpanded] = useState(true);
  const [selectedCitations, setSelectedCitations] = useState<GroundingSource[]>([]);
  const [selectedCitationIndex, setSelectedCitationIndex] = useState(0);
  const [isCitationModalOpen, setIsCitationModalOpen] = useState(false);

  const handleCitationClick = (sources: GroundingSource[], index: number) => {
    setSelectedCitations(sources);
    setSelectedCitationIndex(index);
    setIsCitationModalOpen(true);
  };

  const handleCloseCitationModal = () => {
    setIsCitationModalOpen(false);
    setSelectedCitations([]);
  };

  const scoreConfig = {
    yes: {
      icon: CheckCircle2,
      label: "Met",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-500"
    },
    partially: {
      icon: ({ className }: { className?: string }) => <HalfCircle className={className} />,
      label: "Partially Met",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-500"
    },
    no: {
      icon: XCircle,
      label: "Not Met",
      color: "text-rose-600",
      bgColor: "bg-rose-50",
      borderColor: "border-rose-500"
    },
    insufficient: {
      icon: HelpCircle,
      label: "Insufficient Information",
      color: "text-slate-600",
      bgColor: "bg-slate-50",
      borderColor: "border-slate-400"
    }
  };

  const config = scoreConfig[score];
  const ScoreIcon = config.icon;

  return (
    <ReportCard className="overflow-hidden border border-[#e8e3dc] hover:shadow-lg transition-shadow">
      <div 
        className="p-6 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="p-3 bg-[#f0ebe4] rounded-lg">
              {icon}
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-sans font-semibold text-[#1e3a5f] mb-1">{title} &ndash; {subtitle}</h3>
              {coreQuestion && (
                <p className="text-[#063F6B]/70 text-sm italic mt-0.5">{coreQuestion}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isEditing ? (
              <select
                value={score}
                onChange={(e) => onUpdate?.({ score: e.target.value as Score })}
                onClick={(e) => e.stopPropagation()}
                className="px-3 py-2 text-sm border-2 border-slate-300 rounded-lg bg-white font-semibold focus:outline-none focus:ring-2 focus:ring-[#d4975c]/50"
              >
                <option value="yes">Met</option>
                <option value="partially">Partially Met</option>
                <option value="no">Not Met</option>
                <option value="insufficient">Insufficient Info</option>
              </select>
            ) : (
              <ReportBadge
                variant="outline"
                className={`${config.bgColor} ${config.borderColor} border-2 px-4 py-2`}
              >
                <ScoreIcon className={`w-4 h-4 mr-2 ${config.color}`} />
                <span className={`font-semibold ${config.color}`}>{config.label}</span>
              </ReportBadge>
            )}
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-6 pb-6 space-y-6">
          <div className="h-px bg-slate-200" />
          
          {/* Assessment */}
          <div>
            <h4 className="text-sm font-semibold text-slate-600 mb-2 uppercase tracking-wide">
              Assessment
            </h4>
            {isEditing ? (
              <textarea
                className="w-full text-slate-700 leading-relaxed bg-white border border-slate-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#d4975c]/50 resize-y"
                value={assessment}
                onChange={(e) => onUpdate?.({ assessment: e.target.value })}
                rows={4}
              />
            ) : (
              <p className="text-slate-700 leading-relaxed">
                {assessment}
                <CitationBadges sources={sources} onCitationClick={handleCitationClick} />
              </p>
            )}
          </div>

          {/* Trade-offs */}
          <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
            <h4 className="text-sm font-semibold text-slate-600 mb-4 uppercase tracking-wide">
              Trade-offs
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              {(tradeoff.gain || isEditing) && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-emerald-700 mb-1">WHAT YOU GAIN</div>
                    {isEditing ? (
                      <textarea
                        className="w-full text-slate-700 text-sm bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#d4975c]/50 resize-y"
                        value={tradeoff.gain || ""}
                        onChange={(e) => onUpdate?.({ tradeoff: { ...tradeoff, gain: e.target.value } })}
                        rows={2}
                      />
                    ) : (
                      <p className="text-slate-700 text-sm">{tradeoff.gain}</p>
                    )}
                  </div>
                </div>
              )}
              {(tradeoff.giveUp || isEditing) && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-rose-500 mt-2" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-rose-700 mb-1">WHAT YOU GIVE UP</div>
                    {isEditing ? (
                      <textarea
                        className="w-full text-slate-700 text-sm bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#d4975c]/50 resize-y"
                        value={tradeoff.giveUp || ""}
                        onChange={(e) => onUpdate?.({ tradeoff: { ...tradeoff, giveUp: e.target.value } })}
                        rows={2}
                      />
                    ) : (
                      <p className="text-slate-700 text-sm">{tradeoff.giveUp}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Questions */}
          {questions && questions.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                <HelpCircle className="w-4 h-4" />
                Questions for Vendor
              </h4>
              <ul className="space-y-2">
                {questions.map((question, index) => (
                  <li key={index} className="text-slate-700 text-sm flex gap-2">
                    <span className="text-blue-600 font-semibold flex-shrink-0">{index + 1}.</span>
                    <span>{question}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      )}

      {/* Citation Modal */}
      <CitationModal
        isOpen={isCitationModalOpen}
        sources={selectedCitations}
        citationIndex={selectedCitationIndex}
        onClose={handleCloseCitationModal}
      />
    </ReportCard>
  );
}
